// src/services/VesselApiService.js

const API_KEY = process.env.REACT_APP_MAERSK_API_KEY;
const BASE_URL = 'https://api.maersk.com/schedules';

// Helper to get date range - 2 weeks back and 24 weeks forward
const getDateRange = () => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 14); // 2 weeks back
  
  // Format dates as YYYY-MM-DD
  const formattedStartDate = startDate.toISOString().split('T')[0];
  
  return {
    startDate: formattedStartDate,
    dateRange: 'P24W'  // 24 weeks forward
  };
};

// Format date for display
export const formatDate = (dateTimeStr, isActual = false) => {
  if (!dateTimeStr) return { text: 'N/A', type: 'EST' };
  const date = new Date(dateTimeStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' });
  return {
    text: `${day} ${month}`,
    type: isActual ? 'ACT' : 'EST'
  };
};

export class VesselApiService {
  // Main function to get service schedule
  static async getServiceSchedule(serviceConfig) {
    try {
      const vessels = new Map(); // Use Map to track unique vessels by IMO
      const dateParams = getDateRange();

      // First get all vessels for the service
      const vesselUrl = `${BASE_URL}/vessels?carrierServiceCode=${serviceConfig.code}`;
      console.log('Fetching vessels from:', vesselUrl);

      const vesselResponse = await fetch(vesselUrl, {
        headers: {
          'Consumer-Key': API_KEY
        }
      });

      if (!vesselResponse.ok) {
        throw new Error(`API Error: ${vesselResponse.status} - ${await vesselResponse.text()}`);
      }

      const vesselData = await vesselResponse.json();
      console.log('Vessel data:', vesselData);

      // Now get schedules for each vessel
      for (const vessel of vesselData.vessels || []) {
        const scheduleUrl = `${BASE_URL}/schedules?vesselIMONumber=${vessel.vesselIMONumber}&carrierServiceCode=${serviceConfig.code}&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`;
        console.log('Fetching schedule from:', scheduleUrl);

        const scheduleResponse = await fetch(scheduleUrl, {
          headers: {
            'Consumer-Key': API_KEY
          }
        });

        if (!scheduleResponse.ok) {
          console.warn(`Failed to fetch schedule for vessel ${vessel.vesselIMONumber}: ${await scheduleResponse.text()}`);
          continue;
        }

        const scheduleData = await scheduleResponse.json();
        console.log('Schedule data:', scheduleData);

        // Process schedule data
        if (scheduleData.schedule) {
          vessels.set(vessel.vesselIMONumber, {
            name: vessel.vesselName,
            imo: vessel.vesselIMONumber,
            schedule: scheduleData.schedule
          });
        }
      }

      return Array.from(vessels.values());
    } catch (error) {
      console.error('Error fetching service schedule:', error);
      throw error;
    }
  }
}

// Function to get all active vessels (name and IMO) using DCSA v1 API
export const getAllActiveVessels = async () => {
  try {
    // Use DCSA v1 API to get vessels from all active services
    const services = ['278', '251', '160', 'MW1']; // SAECS, PROTEA, SAFARI, MESAWA
    const allVessels = [];

    for (const serviceCode of services) {
      try {
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(today.getDate() - 60); // 60 days back

        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 120); // 120 days forward

        const url = `https://api.maersk.com/ocean/commercial-schedules/dcsa/v1/vessel-schedules?carrierServiceCode=${serviceCode}&startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}`;

        console.log('Fetching vessels for service:', serviceCode);

        const response = await fetch(url, {
          headers: {
            'API-Version': '1',
            'Consumer-Key': API_KEY
          }
        });

        if (!response.ok) {
          console.warn(`Failed to fetch vessels for service ${serviceCode}: ${response.status}`);
          continue;
        }

        const data = await response.json();

        // DCSA v1 API returns an array with service info as first element
        const serviceData = Array.isArray(data) ? data[0] : data;

        if (serviceData && serviceData.vesselSchedules) {
          serviceData.vesselSchedules.forEach(vesselSchedule => {
            if (vesselSchedule.vessel) {
              allVessels.push({
                vesselName: vesselSchedule.vessel.name || vesselSchedule.vessel.vesselLongName,
                vesselIMONumber: vesselSchedule.vessel.vesselIMONumber,
                serviceCode: serviceCode,
                serviceName: serviceData.carrierServiceName || 'Unknown'
              });
            }
          });
        }
      } catch (serviceError) {
        console.warn(`Error fetching service ${serviceCode}:`, serviceError);
        // Continue with next service
      }
    }

    console.log('Total active vessels found:', allVessels.length);
    return allVessels;
  } catch (error) {
    console.error('Error fetching active vessels:', error);
    throw error;
  }
};

// Function to get schedule by IMO number
export const getScheduleByIMO = async (imoNumber) => {
   try {
      const dateParams = getDateRange();
      // Use the /vessel-schedules endpoint with vesselIMONumber and carrierCodes
      const scheduleUrl = `${BASE_URL}/vessel-schedules?vesselIMONumber=${imoNumber}&carrierCodes=MAEU&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`;
      console.log('Fetching schedule by IMO from:', scheduleUrl);

      const scheduleResponse = await fetch(scheduleUrl, {
        headers: {
          'Consumer-Key': API_KEY
        }
      });

      if (!scheduleResponse.ok) {
        if (scheduleResponse.status === 404) {
          console.log(`Schedule not found for IMO ${imoNumber}.`);
          return null; 
        }
        throw new Error(`API Error: ${scheduleResponse.status} - ${await scheduleResponse.text()}`);
      }

      const scheduleData = await scheduleResponse.json();
      console.log(`Schedule data for IMO ${imoNumber}:`, scheduleData);
      return scheduleData; 

    } catch (error) {
      console.error(`Error fetching schedule for IMO ${imoNumber}:`, error);
      throw error; 
    }
};

// Get all specific services with their codes
export const getServices = () => {
  return [
    { code: '110', name: 'SAECS' },
    { code: '120', name: 'AMEX' },
    { code: '130', name: 'PROTEA' },
    { code: '160', name: 'SAFARI' },
    { code: '170', name: 'MESAWA' }
  ];
};

// Common port codes for quick selection
export const commonPorts = [
  { code: 'ZADUR', name: 'Durban, South Africa' },
  { code: 'ZACPT', name: 'Cape Town, South Africa' },
  { code: 'ZAELZ', name: 'Port Elizabeth, South Africa' },
  { code: 'NLRTM', name: 'Rotterdam, Netherlands' },
  { code: 'GBFXT', name: 'Felixstowe, UK' },
  { code: 'BEANR', name: 'Antwerp, Belgium' },
  { code: 'SGSIN', name: 'Singapore' },
  { code: 'CNSHA', name: 'Shanghai, China' },
  { code: 'USNYC', name: 'New York, USA' },
  { code: 'USHOU', name: 'Houston, USA' },
  { code: 'JPTYO', name: 'Tokyo, Japan' }
];

// South African ports for origin selection
const southAfricanPorts = commonPorts.filter(port => 
  port.name.includes('South Africa')
);

// Function to search for routes between two ports
export const searchPortToPort = async (originPortCode, destinationPortCode) => {
  try {
    if (!originPortCode || !destinationPortCode) {
      throw new Error('Both origin and destination port codes are required');
    }
    
    let originPortName = '';
    let destinationPortName = '';
    
    // Get port names if available
    const originPort = commonPorts.find(p => p.code === originPortCode);
    const destinationPort = commonPorts.find(p => p.code === destinationPortCode);
    
    if (originPort) originPortName = originPort.name;
    if (destinationPort) destinationPortName = destinationPort.name;
    
    console.log(`Searching for routes between ${originPortCode} and ${destinationPortCode}`);
    
    // First, get vessels at the origin port using our existing function that works
    console.log(`Searching for vessels at origin port ${originPortCode}`);
    const originResults = await searchByPort(originPortCode);
    
    // Check if there was an error with origin search
    if (originResults.error) {
      console.log(`Error searching origin port: ${originResults.error}`);
      return {
        searchType: 'port-to-port',
        originPort: {
          code: originPortCode,
          name: originPortName || originPortCode
        },
        destinationPort: {
          code: destinationPortCode,
          name: destinationPortName || destinationPortCode
        },
        routes: []
      };
    }
    
    if (!originResults.vessels || originResults.vessels.length === 0) {
      console.log(`No vessels found at origin port ${originPortCode}`);
      return {
        searchType: 'port-to-port',
        originPort: {
          code: originPortCode,
          name: originPortName || originPortCode
        },
        destinationPort: {
          code: destinationPortCode,
          name: destinationPortName || destinationPortCode
        },
        routes: []
      };
    }
    
    console.log(`Found ${originResults.vessels.length} vessels at origin port ${originPortCode}`);
    
    // Next, get vessels at the destination port
    console.log(`Searching for vessels at destination port ${destinationPortCode}`);
    const destResults = await searchByPort(destinationPortCode);
    
    // Check if there was an error with destination search
    if (destResults.error) {
      console.log(`Error searching destination port: ${destResults.error}`);
      return {
        searchType: 'port-to-port',
        originPort: {
          code: originPortCode,
          name: originPortName || originPortCode
        },
        destinationPort: {
          code: destinationPortCode,
          name: destinationPortName || destinationPortCode
        },
        routes: []
      };
    }
    
    if (!destResults.vessels || destResults.vessels.length === 0) {
      console.log(`No vessels found at destination port ${destinationPortCode}`);
      return {
        searchType: 'port-to-port',
        originPort: {
          code: originPortCode,
          name: originPortName || originPortCode
        },
        destinationPort: {
          code: destinationPortCode,
          name: destinationPortName || destinationPortCode
        },
        routes: []
      };
    }
    
    console.log(`Found ${destResults.vessels.length} vessels at destination port ${destinationPortCode}`);
    
    // Find vessels that call at both ports
    const routes = [];
    const originVessels = new Map();
    
    // Create a map of vessels at origin port for quick lookup
    for (const vessel of originResults.vessels) {
      if (vessel.imo) {
        originVessels.set(vessel.imo, {
          imo: vessel.imo,
          name: vessel.name,
          service: vessel.service,
          serviceCode: vessel.serviceCode,
          eta: vessel.eta,
          etd: vessel.etd
        });
      }
    }
    
    // Check each destination vessel to see if it also calls at origin
    for (const destVessel of destResults.vessels) {
      if (destVessel.imo && originVessels.has(destVessel.imo)) {
        const originVessel = originVessels.get(destVessel.imo);
        
        // Create a valid route if the dates make sense
        const originDepartureDate = new Date(originVessel.etd || '');
        const destArrivalDate = new Date(destVessel.eta || '');
        
        // Only create a route if origin departure is before destination arrival and both dates are valid
        if (originVessel.etd && destVessel.eta && !isNaN(originDepartureDate) && !isNaN(destArrivalDate) && originDepartureDate < destArrivalDate) {
          const transitDays = Math.ceil((destArrivalDate - originDepartureDate) / (1000 * 60 * 60 * 24));
          
          routes.push({
            vessel: {
              imo: destVessel.imo,
              name: destVessel.name,
              service: destVessel.service || originVessel.service || 'Unknown',
              serviceCode: destVessel.serviceCode || originVessel.serviceCode || 'N/A'
            },
            origin: {
              port: originPortCode,
              portName: originResults.portName,
              departureDate: originVessel.etd,
              isActualDeparture: false // We don't have this info, assume estimated
            },
            destination: {
              port: destinationPortCode,
              portName: destResults.portName,
              arrivalDate: destVessel.eta,
              isActualArrival: false // We don't have this info, assume estimated
            },
            transitDays: transitDays
          });
        }
      }
    }
    
    // If we didn't find any direct routes with proper dates, just show vessels that call at both ports
    if (routes.length === 0) {
      console.log("No direct routes found with proper dates, showing common vessels...");
      
      for (const destVessel of destResults.vessels) {
        if (destVessel.imo && originVessels.has(destVessel.imo)) {
          const originVessel = originVessels.get(destVessel.imo);
          
          // Add a route entry even if dates don't line up
          routes.push({
            vessel: {
              imo: destVessel.imo,
              name: destVessel.name,
              service: destVessel.service || originVessel.service || 'Unknown',
              serviceCode: destVessel.serviceCode || originVessel.serviceCode || 'N/A'
            },
            origin: {
              port: originPortCode,
              portName: originResults.portName,
              departureDate: originVessel.etd || 'TBD',
              isActualDeparture: false
            },
            destination: {
              port: destinationPortCode,
              portName: destResults.portName,
              arrivalDate: destVessel.eta || 'TBD',
              isActualArrival: false
            },
            transitDays: 'TBD'
          });
        }
      }
    }
    
    // Prioritize Safari service routes (code 160)
    routes.sort((a, b) => {
      // First sort by Safari service
      if (a.vessel.serviceCode === '160' && b.vessel.serviceCode !== '160') return -1;
      if (a.vessel.serviceCode !== '160' && b.vessel.serviceCode === '160') return 1;
      
      // Then sort by departure date
      const dateA = typeof a.origin.departureDate === 'string' ? new Date(a.origin.departureDate) : null;
      const dateB = typeof b.origin.departureDate === 'string' ? new Date(b.origin.departureDate) : null;
      
      if (dateA && dateB) return dateA - dateB;
      if (dateA) return -1;
      if (dateB) return 1;
      return 0;
    });
    
    console.log(`Found ${routes.length} route(s) from ${originPortCode} to ${destinationPortCode}`);
    
    return {
      searchType: 'port-to-port',
      originPort: {
        code: originPortCode,
        name: originPortName || originPortCode
      },
      destinationPort: {
        code: destinationPortCode,
        name: destinationPortName || destinationPortCode
      },
      routes: routes
    };
  } catch (error) {
    console.error(`Error searching port-to-port routes (${originPortCode} to ${destinationPortCode}):`, error);
    throw error;
  }
};

// Original function - keeping for backward compatibility
export const searchByPort = async (portCode) => {
  try {
    if (!portCode) {
      throw new Error('Port code is required');
    }
    
    const dateParams = getDateRange();
    let portName = '';
    
    // Look for port name in common ports list
    const commonPort = commonPorts.find(p => 
      p.code.toLowerCase() === portCode.toLowerCase() || 
      p.name.toLowerCase().includes(portCode.toLowerCase())
    );
    
    if (commonPort) {
      portCode = commonPort.code;
      portName = commonPort.name;
    }
    
    // Normalize port code to uppercase
    const normalizedPortCode = portCode.toUpperCase();
    console.log(`Searching for vessels calling at port: ${normalizedPortCode}`);
    
    // Build URL for port-based search
    const url = `${BASE_URL}/port-schedules?facilityLocationCode=${normalizedPortCode}&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`;
    
    const response = await fetch(url, {
      headers: {
        'Consumer-Key': API_KEY
      }
    });
    
    // Handle 404 errors by returning empty results instead of throwing
    if (response.status === 404) {
      console.log(`No data found for port ${normalizedPortCode} - API returned 404`);
      return {
        searchType: 'port',
        portCode: normalizedPortCode,
        portName: portName || normalizedPortCode,
        vessels: []
      };
    }
    
    if (!response.ok) {
      throw new Error(`API returned status ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Port search results:', data);
    
    if (!data.vessels || !Array.isArray(data.vessels)) {
      return {
        searchType: 'port',
        portCode: normalizedPortCode,
        portName: portName || normalizedPortCode,
        vessels: []
      };
    }
    
    // Process vessels to extract needed information
    const vessels = data.vessels.map(vessel => ({
      imo: vessel.vesselIMONumber,
      name: vessel.vesselName,
      eta: vessel.estimatedTimeOfArrival,
      etd: vessel.estimatedTimeOfDeparture,
      service: vessel.service?.carrierServiceName || 'Unknown',
      serviceCode: vessel.service?.carrierServiceCode || 'N/A',
      portName: data.portFacility?.name || normalizedPortCode
    }));
    
    return {
      searchType: 'port',
      portCode: normalizedPortCode,
      portName: portName || data.portFacility?.name || normalizedPortCode,
      vessels
    };
  } catch (error) {
    console.error(`Error searching vessels by port (${portCode}):`, error);
    // Return empty results instead of throwing
    return {
      searchType: 'port',
      portCode: portCode.toUpperCase(),
      portName: portCode,
      vessels: [],
      error: error.message
    };
  }
};

// Function to search vessels by name
export const searchVesselsByName = async (vesselName) => {
  try {
    if (!vesselName) {
      throw new Error('Vessel name is required');
    }

    // First, search active vessels to find matching vessels
    const activeVessels = await getAllActiveVessels();
    
    if (!Array.isArray(activeVessels)) {
      console.warn('Active vessels response is not an array:', activeVessels);
      throw new Error('Invalid response from vessel API');
    }
    
    // Filter vessels by name (case-insensitive partial match)
    const matches = activeVessels.filter(vessel => 
      vessel.vesselName.toLowerCase().includes(vesselName.toLowerCase())
    );
    
    console.log(`Found ${matches.length} vessels matching "${vesselName}"`);
    
    // Return search results
    return {
      searchType: 'vessel',
      searchTerm: vesselName,
      matches
    };
  } catch (error) {
    console.error(`Error searching vessels by name (${vesselName}):`, error);
    throw error;
  }
};

// Function to find schedules between two ports
export const findSchedulesByRoute = async (originPort, destinationPort) => {
  console.log(`Searching for routes from ${originPort} to ${destinationPort} using direct API query.`);
  const dateParams = getDateRange();
  const validSchedules = [];

  try {
    // 1. Make a single API call to find all schedules that include the origin port.
    const url = `${BASE_URL}?portUnlocode=${originPort}&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`;
    console.log(`Querying Maersk API: ${url}`);

    const response = await fetch(url, {
      headers: { 'Consumer-Key': API_KEY }
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${await response.text()}`);
    }

    const data = await response.json();

    if (!data.schedules || data.schedules.length === 0) {
      console.log(`No schedules found from Maersk API for port ${originPort}`);
      return [];
    }

    // 2. Filter the results in memory to find valid routes.
    for (const schedule of data.schedules) {
      const portCalls = schedule.vesselCalls;
      if (!portCalls || portCalls.length < 2) continue;

      // Find the index of the origin and destination ports in the call sequence
      const originIndex = portCalls.findIndex(call => call.facility.location.unlocode.toLowerCase() === originPort.toLowerCase());
      const destinationIndex = portCalls.findIndex(call => call.facility.location.unlocode.toLowerCase() === destinationPort.toLowerCase());

      // 3. Check if the route is valid (destination comes after origin)
      if (originIndex !== -1 && destinationIndex !== -1 && destinationIndex > originIndex) {
        const originCall = portCalls[originIndex];
        const destinationCall = portCalls[destinationIndex];

        // Extract departure from origin and arrival at destination
        const departure = originCall.callSchedules.find(s => s.transportEventTypeCode === 'DEPA')?.classifierDateTime?.dateTime;
        const arrival = destinationCall.callSchedules.find(s => s.transportEventTypeCode === 'ARRI')?.classifierDateTime?.dateTime;

        // Only add if we have both departure and arrival times
        if (departure && arrival) {
          validSchedules.push({
            vesselName: schedule.vessel.vesselName,
            origin_port: originPort,
            destination_port: destinationPort,
            departure_date: departure,
            arrival_date: arrival,
          });
        }
      }
    }

    console.log(`Found ${validSchedules.length} valid schedules from ${originPort} to ${destinationPort}.`);
    return validSchedules;

  } catch (error) {
    console.error(`Error in findSchedulesByRoute from ${originPort} to ${destinationPort}:`, error);
    return []; // Return empty array on error
  }
};